/**
 * StaffTable — Overview table of all employees with optional selection
 */

import clsx from 'clsx';
import { Badge } from '../ui';
import type { Staff } from '../../stores/workforce-store';

interface StaffTableProps {
  staff: Staff[];
  onSelect: (staff: Staff) => void;
  isLoading: boolean;
  selectable?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onToggleAll?: () => void;
}

const contractLabels: Record<string, string> = {
  vast: 'Vast',
  jaaruren: 'Jaaruren',
  'min-max': 'Min-max',
  '0-uren': '0-uren',
  student: 'Student',
  zzp: 'ZZP',
};

const teamLabels: Record<string, string> = {
  wasserij: 'Wasserij',
  route: 'Route',
  kantoor: 'Kantoor',
  algemeen: 'Algemeen',
};

function PinStatus({ staff }: { staff: Staff }) {
  const hasEmail = !!staff.email;
  const hasPin = !!staff.pin_code;

  if (!hasEmail) {
    return <span className="text-xs text-slate-400">Geen e-mail</span>;
  }
  if (!hasPin) {
    return <span className="text-xs text-amber-500">Geen PIN</span>;
  }
  return <span className="text-xs text-emerald-500">Klaar</span>;
}

export function StaffTable({ staff, onSelect, isLoading, selectable, selectedIds, onToggleSelect, onToggleAll }: StaffTableProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (staff.length === 0) {
    return (
      <p className="text-muted text-center py-12">Geen medewerkers gevonden</p>
    );
  }

  const allSelected = selectedIds && staff.length > 0 && staff.every(s => selectedIds.has(s.id));
  const someSelected = selectedIds && staff.some(s => selectedIds.has(s.id));

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-gray-200">
            {selectable && (
              <th className="pb-3 pr-3 w-10">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = !allSelected && !!someSelected; }}
                  onChange={onToggleAll}
                  className="rounded border-gray-300 text-indigo-500 focus:ring-indigo-500"
                />
              </th>
            )}
            <th className="pb-3 text-xs font-medium text-muted uppercase tracking-wider">Naam</th>
            <th className="pb-3 text-xs font-medium text-muted uppercase tracking-wider">Contract</th>
            <th className="pb-3 text-xs font-medium text-muted uppercase tracking-wider">Team</th>
            <th className="pb-3 text-xs font-medium text-muted uppercase tracking-wider">Uren/week</th>
            {selectable && (
              <th className="pb-3 text-xs font-medium text-muted uppercase tracking-wider">PIN-status</th>
            )}
            <th className="pb-3 text-xs font-medium text-muted uppercase tracking-wider">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {staff.map(member => (
            <tr
              key={member.id}
              onClick={() => !selectable ? onSelect(member) : undefined}
              className={clsx(
                'transition-colors',
                !selectable && 'hover:bg-gray-50 cursor-pointer',
                selectable && selectedIds?.has(member.id) && 'bg-indigo-50/50',
              )}
            >
              {selectable && (
                <td className="py-3.5 pr-3">
                  <input
                    type="checkbox"
                    checked={selectedIds?.has(member.id) || false}
                    onChange={() => onToggleSelect?.(member.id)}
                    className="rounded border-gray-300 text-indigo-500 focus:ring-indigo-500"
                  />
                </td>
              )}
              <td className="py-3.5">
                <div className="flex items-center gap-3">
                  <div className={clsx(
                    'w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold',
                    member.is_active ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-400'
                  )}>
                    {member.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-main flex items-center gap-2">
                      {selectable ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); onSelect(member); }}
                          className="hover:text-indigo-600 hover:underline"
                        >
                          {member.name}
                        </button>
                      ) : member.name}
                      {member.onboarding_status === 'completed' && !member.onboarding_seen_at && (
                        <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full">
                          Nieuw
                        </span>
                      )}
                      {member.onboarding_status === 'invited' && (
                        <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">
                          Uitgenodigd
                        </span>
                      )}
                    </p>
                    {member.email && (
                      <p className="text-xs text-muted">{member.email}</p>
                    )}
                  </div>
                </div>
              </td>
              <td className="py-3.5">
                <span className="text-sm text-main">
                  {contractLabels[member.contract_type] || member.contract_type}
                </span>
              </td>
              <td className="py-3.5">
                <span className="text-sm text-main">
                  {member.team ? teamLabels[member.team] || member.team : '-'}
                </span>
              </td>
              <td className="py-3.5">
                <span className="text-sm text-main">
                  {member.uren_per_week ? `${member.uren_per_week}u` : '-'}
                </span>
              </td>
              {selectable && (
                <td className="py-3.5">
                  <PinStatus staff={member} />
                </td>
              )}
              <td className="py-3.5">
                <Badge variant={member.is_active ? 'success' : 'default'}>
                  {member.is_active ? 'Actief' : 'Inactief'}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
